import { IconClock } from "app/components/Icon/Icon";
import type { EventType } from "app/types/eventType";
import React, { useCallback, useEffect, useState } from "react";
import "./AsianEventIcons.scss";
import AsianEventIconsBetsGraphs from "./components/AsianEventIconsBetsGraphs/AsianEventIconsBetsGraphs";
import AsianEventIconsLiveEvents from "./components/AsianEventIconsLiveEvents/AsianEventIconsLiveEvents";

// Define the Props type
type Props = {
  event: EventType;
  toggleAddRemoveToFavorites: (eventId: number) => void;
  onClickGraph: (e: React.MouseEvent<HTMLElement>) => void;
};

const AsianEventIcons: React.FC<Props> = ({
  event,
  toggleAddRemoveToFavorites,
  onClickGraph,
}) => {
  const [favouriteSelected, setFavouriteSelected] = useState(event.favourite);

  // Use the useCallback hook to memoize the function.
  // Memoize the addRemoveFavourite function to prevent unnecessary re-renders
  const addRemoveFavourite = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      // Toggle State
      setFavouriteSelected(prev => !prev);
      toggleAddRemoveToFavorites(event.id);
    },
    [toggleAddRemoveToFavorites, event.id]
  );

  // Update favouriteSelected state when event.favourite changes
  useEffect(() => {
    setFavouriteSelected(event.favourite);
  }, [event.favourite]);

  // Determine the liveEvents element based on event.allowLiveBetting
  const liveEvents = event?.allowLiveBetting ? (
    <IconClock className="icon-desktop--inactive asian-event__icon asian-event__event-clock-icon" />
  ) : (
    <span className="deadball__icon"></span>
  );

  return (
    <div className="asian-event__icons">
      <AsianEventIconsLiveEvents
        liveEvents={liveEvents}
        addRemoveFavourite={addRemoveFavourite}
        favouriteSelected={favouriteSelected}
      />
      <AsianEventIconsBetsGraphs
        event={event}
        onClickGraph={useCallback(
          (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            onClickGraph(e);
          },
          [onClickGraph]
        )}
      />
    </div>
  );
};

// Function to check if specific properties of the event object have changed
const shouldSkipUpdate = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.event.favourite === nextProps.event.favourite &&
    prevProps.event.hasBetsOnEvent === nextProps.event.hasBetsOnEvent
  );
};

// Export the component wrapped with React.memo to optimize performance
export default React.memo(AsianEventIcons, shouldSkipUpdate);
